import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { visuallyHidden } from '@mui/utils';
import SearchIcon from '@mui/icons-material/Search';
import {getServiceRequestWithJiraProjectById} from "../../utilities/ServiceRequestAPI";
import keycloak from "../../Keycloak";
import {useCallback, useEffect, useState} from "react";
import {styled} from "@mui/material/styles";
import {InputAdornment, Select, TableFooter, TextField} from "@mui/material";
import Pagination from '@mui/material/Pagination';
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import TableHead from "@mui/material/TableHead";
import { makeStyles } from "@mui/styles";

function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}
function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) {
            return order;
        }
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

const headCells = [
    {
        id: 'date',
        numeric: false,
        disablePadding: false,
        label: 'Date',
    },
    {
        id: 'key',
        numeric: false,
        disablePadding: false,
        label: 'Key',
    },
    {
        id: 'type',
        numeric: false,
        disablePadding: false,
        label: 'Issue Type',
    },
    {
        id: 'summary',
        numeric: false,
        disablePadding: false,
        label: 'Summary',
    },
    {
        id: 'assignee',
        numeric: false,
        disablePadding: false,
        label: 'Assignee',
    },
    {
        id: 'status',
        numeric: false,
        disablePadding: false,
        label: 'Status',
    }
];

const DEFAULT_ORDER = 'asc';
const DEFAULT_ORDER_BY = 'calories';
const DEFAULT_ROWS_PER_PAGE = 5;

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.common.hover,
        color: theme.palette.common.black,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const useStyles = makeStyles({
    root: {
        "& .MuiOutlinedInput-root": {
            "&.Mui-focused fieldset": {
                borderColor: '#EC8D2B',
                borderWidth: 0,
            }
        }
    },
    select: {
        "&.MuiOutlinedInput-root": {
            "&.Mui-focused fieldset": {
                borderColor: '#EC8D2B'
            }
        }
    },
})


function EnhancedTableHead(props) {
    const {order, orderBy } =
        props;

    return (
        <TableHead data-testid='tableHead' style={tableHeaderStyle}>
            <TableRow>
                {headCells.map((headCell) => (
                    <StyledTableCell
                        key={headCell.id}
                        align={headCell.numeric ? 'right' : 'left'}
                        padding={headCell.disablePadding ? 'none' : 'normal'}
                        sortDirection={orderBy === headCell.id ? order : false}
                    >

                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <Box component="span" sx={visuallyHidden}>
                                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                </Box>
                            ) : null}

                    </StyledTableCell>
                ))}
            </TableRow >
        </TableHead
        >
    );
}

const FlexContainer = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
});

const RightAlignFlexContainer = styled(FlexContainer)({
    justifyContent: 'flex-end', // Align items to the right
});

const LeftAlignFlexContainer = styled(FlexContainer)({
    justifyContent: 'flex-start', // Align items to the left
});

const LeftBottomFlexContainer = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start', // Align items to the left
});

const FooterFlexContainer = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end', // Align items to the right
});

const tableHeaderStyle = {
    backgroundColor: '#DDDFDF',
    height: '56px',
};

EnhancedTableHead.propTypes = {
    numSelected: PropTypes.number.isRequired,
    onRequestSort: PropTypes.func.isRequired,

    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
};

export default function EnhancedTable({serviceRequestId}) {

    const [rowData, setRowData] = useState([]);
    const getJiraIssueData = useCallback(() => {
        getServiceRequestWithJiraProjectById(serviceRequestId, keycloak.token)
            .then((jiraIssueData) => {
                setRowData(jiraIssueData)
            }).catch((e) => {
            console.log(e.message)
            });
    },[serviceRequestId])
    useEffect(() => {
        getJiraIssueData();
    }, [getJiraIssueData]);

    let getRows = rowData;

    const [order, setOrder] = useState(DEFAULT_ORDER);
    const [orderBy, setOrderBy] = useState(DEFAULT_ORDER_BY);
    const [selected] = useState([]);
    const [page, setPage] = useState(0);
    const [dense] = useState(false);
    const [visibleRows, setVisibleRows] = useState(getRows);

    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);


    const handleRowsPerPageChange = (event) => {
        const newRowsPerPage = event.target.value;
        setRowsPerPage(newRowsPerPage);
        setPage(0);
        const startIndex = page * newRowsPerPage;
        const endIndex = startIndex + newRowsPerPage;
        setVisibleRows(getRows.slice(startIndex, endIndex));
    };

    const requestSearch = (searchedVal) => {
        console.log(`Value: `, searchedVal);

        const filteredRows = getRows.filter((row) => {
            return Object.values(row).some((value) =>
                String(value).toLowerCase().includes(searchedVal.toLowerCase())
            );
        });

        const limitedRows = filteredRows.slice(0, rowsPerPage);

        setVisibleRows(limitedRows);
    };

    useEffect(() => {
        const sortedRows = stableSort(rowData, getComparator(order, orderBy));

        const startIndex = page * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;

        const rowsOnPage = sortedRows.slice(startIndex, endIndex);
        setVisibleRows(rowsOnPage);
        let table = document.getElementById("Table");
        if(rowData.length === 0)
        {
            table.style.display = "none";
        }
        else if(rowData.length > 0) {
            table.style.display = "block";
        }
    }, [rowData, order, orderBy, page, rowsPerPage]);



    const handleRequestSort = useCallback(
        (event, newOrderBy) => {
            const isAsc = orderBy === newOrderBy && order === 'asc';
            const toggledOrder = isAsc ? 'desc' : 'asc';
            setOrder(toggledOrder);
            setOrderBy(newOrderBy);

            const sortedRows = stableSort(rowData, getComparator(toggledOrder, newOrderBy));
            const updatedRows = sortedRows.slice(
                page * rowsPerPage,
                page * rowsPerPage + rowsPerPage,
            );
            setVisibleRows(updatedRows);
        },
        [order, orderBy, page, rowsPerPage, rowData],
    );


    const handleChange = (event, value) => {
        const newPage = typeof value === 'number' ? value - 1 : value;
        const startIndex = newPage * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        const rowsOnPage = stableSort(rowData, getComparator(order, orderBy)).slice(startIndex, endIndex);
        setVisibleRows(rowsOnPage);
    };

    const displayedRowsCount = Math.min((page + 1) * rowsPerPage, rowData.length);
    const totalRowsCount = rowData.length;

    const classes = useStyles();

    return (
        <Box sx={{ width: '100%'}} id={"Table"}>
            <Paper sx={{ width: '100%', mb: 2, borderRadius: '15px'}}>
                <Box sx={{ flexGrow: 1 }}>
                    <FlexContainer height={70}>
                        <LeftAlignFlexContainer>
                            <InputLabel id="rows-per-page-label" sx={{ marginLeft: '16px' }}>
                                Show
                            </InputLabel>
                            <FormControl sx={{ marginLeft: '15px', minWidth: '55px', marginRight: '7px'}}>
                                <Select
                                    labelId="rows-per-page-label"
                                    id="rows-per-page-select"
                                    value={rowsPerPage}
                                    sx={{height: '40px', width: '50px', boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: 0 }}}
                                    onChange={handleRowsPerPageChange}
                                    label="Show rows per page"
                                    disableUnderline={true}
                                    variant={"standard"}
                                >
                                    <MenuItem value={5}>5</MenuItem>
                                    <MenuItem value={10}>10</MenuItem>
                                    <MenuItem value={25}>25</MenuItem>
                                </Select>
                            </FormControl>
                            <InputLabel id="rows-per-page-label">entries</InputLabel>
                        </LeftAlignFlexContainer>
                        <RightAlignFlexContainer>
                            <div className={"ticketSearchBar"}>
                                <TextField
                                    data-testid="ticketSearchBar"
                                    placeholder="Search..."
                                    type="search"
                                    className={classes.root}
                                    sx={{marginRight: '10px'}}
                                    onInput={(e) => requestSearch(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </div>
                        </RightAlignFlexContainer>
                    </FlexContainer>
                </Box>

                <TableContainer maxheight={800}>
                    <Table
                        sx={{ minWidth: 750 }}
                        aria-labelledby="tableTitle"
                        size={dense ? 'small' : 'medium'}
                    >
                        <EnhancedTableHead
                            numSelected={selected.length}
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={handleRequestSort}
                            rowCount={rowData.length}
                        />
                        <TableBody>
                            {visibleRows.map((row, index) => (
                                        <TableRow
                                            hover
                                            role="checkbox"
                                            tabIndex={-1}
                                            key={index}
                                            sx={{ cursor: 'pointer' }}
                                        >
                                            <StyledTableCell align="left">{new Date(row.issuDate).toLocaleDateString(undefined, { year: 'numeric', month: 'numeric', day: 'numeric' })}</StyledTableCell>
                                            <StyledTableCell align="left">{row.projectKey}</StyledTableCell>
                                            <StyledTableCell align="left">{row.issueType}</StyledTableCell>
                                            <StyledTableCell align="left">{row.summary}</StyledTableCell>
                                            <StyledTableCell align="left">{row.assignee}</StyledTableCell>
                                            <StyledTableCell align="left">{row.status}</StyledTableCell>
                                        </TableRow>
                            ))}
                    </TableBody>
                    </Table>
                    <FlexContainer>
                        <LeftBottomFlexContainer>
                            <InputLabel id="display-rows-label" sx={{ marginLeft: '10px', marginRight: '10px' }}>
                                Showing
                            </InputLabel>
                            <div>1 to {displayedRowsCount} of {totalRowsCount}</div>
                            <InputLabel id="display-rows-label" sx={{ marginLeft: '10px'}}>
                                entries
                            </InputLabel>
                        </LeftBottomFlexContainer>

                        <FooterFlexContainer>
                            <TableFooter className={'ticketsPagination'} spacing={2}>
                                <Pagination count={Math.ceil(getRows.length / rowsPerPage)} page={page + 1} onChange={handleChange} />
                            </TableFooter>
                        </FooterFlexContainer>
                    </FlexContainer>
                </TableContainer>
            </Paper>
        </Box>
    );
}